import { useEffect, useRef, useState } from 'react';
import {
    MapContainer,
    TileLayer,
} from 'react-leaflet';
import 'leaflet.fullscreen';
import 'leaflet.fullscreen/dist/Control.FullScreen.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import {AddMarkerToClick, MapEventHandler, SearchMap} from "./Leaflet/MapFunctions";
import {calculateHeight, DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from "../Apis/functions";
import {Box} from "@mui/material";

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * ZoneLeaflet Component
 *
 * A reusable Leaflet map component with support for:
 * - Initial center and zoom
 * - Adding markers on click
 * - Searching locations by address
 * - Fullscreen map control
 * - Optional custom markers and popups
 *
 * @param {Object} props - Component props.
 * @param {Array<number>} props.latlng - Initial map coordinates `[latitude, longitude]`.
 * @param {Function} props.setLatitude - Function to update the latitude value.
 * @param {Function} props.setLongitude - Function to update the longitude value.
 * @param {string} [props.searchLocation=''] - Address or location to search and center the map on.
 * @param {boolean} [props.disabled=false] - If true, disables map click interactions (prevents adding markers).
 * @param {boolean|React.ReactNode} [props.customMarkerPopup=false] - Custom content for marker popups.
 * @param {Array<Object>} [props.markersList=[]] - List of markers to render on the map.
 * Each marker object should contain `{ latitude, longitude }` properties.
 * @param {L.Icon} [props.iconMarker] - Custom Leaflet icon for markers. Defaults to the `ic-marker` pin.
 * @see DEFAULT_MARKER_ICON
 * @param {Function} [props.onMarkerClick] - Callback triggered when a marker is clicked.
 * @param {Function} [props.onMarkerHover] - Callback triggered when a marker is hovered.
 * @param {string|null} [props.classNameMap=null] - Optional CSS class name for the map container.
 * @param {number} [props.marginBottom=60] - Margin (in pixels) used to calculate the dynamic map height.
 * @param {Function} [props.onMapMove] - Callback function triggered when the map finishes moving or zooming.
 *
 * @returns {JSX.Element} A fully functional Leaflet map with dynamic behavior.
 *
 */

const ZoneLeaflet = ({
                         latlng,
                         setLatitude,
                         setLongitude,
                         searchLocation = '',
                         disabled = false,
                         customMarkerPopup = false,
                         markersList = [],
                         iconMarker,
                         onMarkerClick,
                         onMarkerHover,
                         classNameMap = null,
                         marginBottom=60,
                         onMapMove,
                         ifCluster=false
                     }) => {

    const defaultCenter = [DEFAULT_LATITUDE, DEFAULT_LONGITUDE];
    const [center, setCenter] = useState(null);
    const [zoom, setZoom] = useState(5);
    const elementRefMap = useRef(null);
    const [height, setHeight] = useState(null);

    /**
     * Initializes or updates the map center whenever `markersList` or `latlng` change.
     *
     * Priority order for setting the map center:
     * 1. If `markersList` contains markers, use the first marker's coordinates.
     * 2. If `latlng` is a valid coordinate array, use it as the center.
     * 3. Otherwise, fallback to the predefined `defaultCenter`.
     *
     * @effect
     * @dependencies [markersList, latlng]
     */
    useEffect(() => {
        if (markersList.length > 0) {
            const firstMarker = markersList[0];
            if (firstMarker.latitude !== undefined && firstMarker.longitude !== undefined) {
                setCenter([firstMarker.latitude, firstMarker.longitude]);
            }
        } else if (Array.isArray(latlng) && latlng.every(n => !isNaN(n))) {
            setCenter(latlng);
        } else {
            setCenter(defaultCenter);
        }
    }, [markersList, latlng]);

    /**
     * Calculates the map container height dynamically based on the viewport
     * when no custom CSS class (`classNameMap`) is provided.
     *
     * Uses the `calculateHeight` utility to adjust the height relative to
     * the element’s position and bottom margin space.
     *
     * @effect
     * @dependencies []
     */
    useEffect(() => {
        if (classNameMap !== null) return;

        calculateHeight({elementRef: elementRefMap, setHeight, bottom: marginBottom})
    }, []);

    return (
        <div className={"w-100 h-100"} ref={elementRefMap}>
            {(classNameMap || height !== null) && center && <>
                    <Box sx={height !== null ? { height: height } : undefined} className={classNameMap}>
                        <MapContainer
                            center={center}
                            zoom={zoom}
                            minZoom={3}
                            className='w-100 h-100'
                            fullscreenControl={center?.length && !isNaN(center[0])}
                        >
                            <>
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />

                                <MapEventHandler onMapMove={onMapMove} />

                                <AddMarkerToClick
                                    latlng={latlng}
                                    markersList={markersList.length > 0 ? markersList : latlng? [latlng] : []}
                                    setLatitude={setLatitude}
                                    setLongitude={setLongitude}
                                    disabled={disabled}
                                    customMarkerPopup={customMarkerPopup}
                                    iconMarker={iconMarker}
                                    onMarkerClick={onMarkerClick}
                                    onMarkerHover={onMarkerHover}
                                    ifCluster={ifCluster}
                                />
                                <SearchMap
                                    setCenter={setCenter}
                                    searchLocation={searchLocation}
                                    setZoom={setZoom}
                                />
                            </>
                        </MapContainer>
                    </Box>
                </>
            }
        </div>
    );
};

export default ZoneLeaflet;
